import { AxiosError } from "axios";
import { assistedAffiliationClient } from "./assistedAffiliationClient";
import { getAssistedAffiliationHost } from "./getAssistedAffiliationHost";
import {
  ApiEnvelope,
  AssistedAffiliationDraft,
  AssistedAffiliationDraftPayload,
} from "./types";

const assistedAffiliationDraftEndpoint = "/v1/assisted-affiliation/draft";

/**
 * Crea la solicitud con lo que el usuario capturó.
 * @param payload - Ya armado por la pantalla, que es quien puede resolver los nombres de los
 * catálogos a partir de los identificadores que guarda el formulario.
 * @returns El contenido de `data`, que el servicio devuelve vacío: solo confirma la creación, no
 * responde el registro. Relanza el error para que la pantalla lo traduzca.
 */
export const createDraftService = async (
  payload: AssistedAffiliationDraftPayload
): Promise<AssistedAffiliationDraft | null> => {
  const host = await getAssistedAffiliationHost();

  const response = await assistedAffiliationClient.post<
    ApiEnvelope<AssistedAffiliationDraft | null>
  >(`${host}${assistedAffiliationDraftEndpoint}`, payload);

  return response.data.data ?? null;
};

const MAX_SERVER_MESSAGE_LENGTH = 200;

// El mensaje del servicio solo se muestra cuando parece una frase: un HTML o un texto enorme no le dice
// nada al usuario.
const isReadableMessage = (value: string) => {
  const trimmed = value.trim();
  return (
    trimmed.length > 0 &&
    trimmed.length <= MAX_SERVER_MESSAGE_LENGTH &&
    !trimmed.startsWith("<")
  );
};

/**
 * Texto para el aviso cuando una operación sobre la solicitud falla.
 *
 * Solo se muestra el `message` del sobre del servicio (`{ code, message, data }`), que es la frase
 * pensada para el usuario. Todo lo demás se reemplaza por el texto de la pantalla: el `{ message }` suelto con
 * que responde el API Gateway ("Missing Authentication Token"), el detalle interno de una falla no
 * controlada, una página HTML, o la ausencia de respuesta (red, CORS, tiempo agotado).
 */
export const getDraftErrorMessage = (
  error: unknown,
  fallback = "No se pudo crear el comercio."
) => {
  const responseData = (error as AxiosError<unknown> | undefined)?.response?.data;
  if (!responseData || typeof responseData !== "object") return fallback;

  const { code, message } = responseData as { code?: unknown; message?: unknown };
  const isServiceEnvelope = typeof code === "string" && typeof message === "string";

  return isServiceEnvelope && isReadableMessage(message) ? message : fallback;
};
